import React from 'react';
import { Outlet } from 'react-router-dom';
import { motion } from 'framer-motion';
import BackgroundImg from '../../assets/Background/hero_travel_bg.png';
import landingData from '../landingData.json';

const HeroSection = ({ isDesktop, scrollToSection }) => {
  return (
    <section className="relative overflow-hidden border-b border-[#E2E8F0] pt-24 sm:pt-28 md:pt-32 pb-10 sm:pb-16 md:pb-20 bg-cover bg-right" style={{ backgroundImage: `url(${BackgroundImg})` }}>
      {/* Responsive Gradient Overlay: Soft white fade on left, crisp background image visibility on right */}
      <div 
        className="absolute inset-0 pointer-events-none z-0" 
        style={{ 
          background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.82) 0%, rgba(255, 255, 255, 0.50) 50%, rgba(255, 255, 255, 0.20) 100%), linear-gradient(90deg, rgba(255, 255, 255, 0.92) 0%, rgba(255, 255, 255, 0.65) 50%, rgba(255, 255, 255, 0.20) 100%)'
        }}
      ></div>

      <div className="max-w-[1200px] mx-auto px-4 sm:px-6 md:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-14 items-center">

          {/* LEFT HERO TEXT WITH ANIMATION */}
          <motion.div 
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-7 flex flex-col items-start"
          >
            <div className="inline-flex items-center gap-2 text-[10px] sm:text-[11.5px] font-bold tracking-wider uppercase text-[#044E4D] bg-[#E0F2F1] border border-[#067978]/25 px-3 sm:px-3.5 py-1.5 rounded-full mb-4 sm:mb-5">
              <i className="ti ti-building-store text-xs sm:text-sm"></i> For local business owners
            </div>

            <h1 className="text-2xl sm:text-4xl md:text-5xl lg:text-[52px] font-extrabold text-[#0F172A] tracking-tight mb-3 sm:mb-4 leading-tight">
              Where travellers <br className="hidden sm:inline" />
              <span className="text-[#067978]">find you first.</span>
            </h1>

            <p className="text-xs sm:text-base md:text-[17px] text-[#334155] font-medium max-w-[540px] leading-[1.6] mb-6 sm:mb-7">
              Reach high-intent travellers already at your destination — ready to eat, shop, or explore. No upfront cost, no ads to buy, no risk.
            </p>

            {/* Bullet Stack */}
            <div className="flex flex-col gap-3 sm:gap-4 w-full max-w-[540px] mb-6 sm:mb-9">
              {landingData.heroBullets.map((bullet, idx) => (
                <motion.div 
                  key={idx}
                  initial={{ opacity: 0, y: 15 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.2 + idx * 0.1 }}
                  className="flex items-center gap-2.5 sm:gap-4 text-xs sm:text-sm md:text-base font-semibold text-[#0F172A]"
                >
                  <div 
                    className="flex-shrink-0 flex items-center justify-center text-[#067978] shadow-sm w-8 h-8 sm:w-10 sm:h-10 rounded-full"
                    style={{
                      background: '#FFFFFF',
                      border: '1.5px solid rgba(6, 121, 120, 0.28)',
                      boxShadow: '0 4px 10px rgba(6, 121, 120, 0.08)'
                    }}
                  >
                    <i className={`${bullet.iconClass} text-sm sm:text-lg`}></i>
                  </div>
                  <span>{bullet.text}</span>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons Removed as per request */}

            <div className="flex items-center gap-3 sm:gap-8 pt-4 sm:pt-6 border-t border-[#E2E8F0] w-full max-w-[540px] flex-wrap text-[11px] sm:text-sm text-[#334155] font-semibold">
              <div>✦ <strong className="text-[#067978] font-extrabold text-xs sm:text-base">6,500+</strong> local businesses</div>
              <div>✦ <strong className="text-[#067978] font-extrabold text-xs sm:text-base">100+</strong> destinations live</div>
              <div>✦ <strong className="text-[#067978] font-extrabold text-xs sm:text-base">₹0</strong> to join</div>
            </div>

            <div className="hidden sm:flex items-center gap-2 text-[11px] font-extrabold tracking-widest uppercase text-slate-500 pt-6">
              <motion.div 
                animate={{ y: [0, 4, 0] }}
                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                className="w-6 h-6 rounded-full border border-slate-300 flex items-center justify-center text-slate-600"
              >
                <i className="ti ti-chevron-down text-xs"></i>
              </motion.div>
              <span>Scroll to explore</span>
            </div>
          </motion.div>

          {/* RIGHT HERO SIDE: ELEGANT EMBEDDED FORM CARD WITH ENTRANCE ANIMATION */}
          <motion.div 
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="lg:col-span-5 w-full max-w-md mx-auto lg:max-w-none"
          >
            <Outlet />
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default HeroSection;
